import { createTheme } from '@mui/material/styles';

// Sherlock retro-terminal theme — Flavor A (CRT phosphor + Claude amber).
// Spec: docs/superpowers/specs/2026-05-10-sherlock-retro-redesign-design.md
//
// All component code reads from theme.palette.*, theme.spacing(), and
// theme.typography.* — no raw hex outside this file (and global.css).

declare module '@mui/material/styles' {
  interface PaletteColor {
    soft?: string;
    strong?: string;
    glow?: string;
  }
  interface SimplePaletteColorOptions {
    soft?: string;
    strong?: string;
    glow?: string;
  }
  interface TypeBackground {
    deeper: string;
    elev2: string;
  }
  interface TypeText {
    faintest: string;
  }
  interface Palette {
    accent: PaletteColor;
  }
  interface PaletteOptions {
    accent?: SimplePaletteColorOptions;
  }
}

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#D97757',
      light: '#f0a37c',
      dark: '#a85a3f',
      soft: 'rgba(217, 119, 87, 0.10)',
      strong: 'rgba(217, 119, 87, 0.30)',
      glow: 'rgba(217, 119, 87, 0.55)',
      contrastText: '#0A0A0D',
    },
    accent: {
      main: '#D97757',
      light: '#f0a37c',
      dark: '#a85a3f',
      soft: 'rgba(217, 119, 87, 0.10)',
      strong: 'rgba(217, 119, 87, 0.30)',
      glow: 'rgba(217, 119, 87, 0.55)',
      contrastText: '#0A0A0D',
    },
    secondary: {
      main: '#6FA8FF',
    },
    error: { main: '#ef4444' },
    warning: { main: '#FFB000' },
    info: { main: '#6FA8FF' },
    success: { main: '#6BFFAB' },
    background: {
      default: '#0E0E12',
      deeper: '#0A0A0D',
      paper: '#15151b',
      elev2: '#1a1a21',
    },
    text: {
      primary: '#E8DDC9',
      secondary: '#c9c0ad',
      disabled: '#8a8a96',
      faintest: '#6c6c78',
    },
    divider: '#25252e',
  },
  spacing: 8,
  typography: {
    fontFamily: '"Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 14,
    h1: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '1.75rem',
      fontWeight: 700,
      letterSpacing: '-0.3px',
    },
    h2: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '1.125rem',
      fontWeight: 700,
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '0.875rem',
      fontWeight: 600,
      letterSpacing: '0.5px',
      textTransform: 'uppercase',
    },
    h4: { fontWeight: 600, fontSize: '1.5rem' },
    h5: { fontWeight: 600, fontSize: '1.25rem' },
    h6: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '0.875rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, color: '#c9c0ad' },
    body1: { fontSize: '0.875rem', lineHeight: 1.55 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    caption: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontSize: '0.6875rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
    },
    button: {
      fontFamily: '"JetBrains Mono", ui-monospace, monospace',
      fontWeight: 700,
      letterSpacing: '0.3px',
      textTransform: 'none',
    },
  },
  shape: { borderRadius: 4 },
  transitions: {
    duration: {
      shortest: 80,
      shorter: 150,
      short: 180,
      standard: 220,
      complex: 400,
      enteringScreen: 160,
      leavingScreen: 120,
    },
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 3,
          padding: '6px 14px',
          transition: 'transform 80ms ease, box-shadow 120ms ease',
          '&:active': {
            transform: 'translateY(1px) scale(0.98)',
          },
        },
        containedPrimary: {
          color: '#0A0A0D',
          boxShadow: '0 0 0 1px rgba(217, 119, 87, 0.30)',
          '&:hover': {
            backgroundColor: '#f0a37c',
          },
          '&:active': {
            boxShadow: '0 0 0 1px rgba(217, 119, 87, 0.20)',
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          backgroundColor: '#15151b',
          borderColor: '#25252e',
          backgroundImage: 'none',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(217, 119, 87, 0.06)',
          transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 120ms ease',
          '&:hover': {
            borderColor: '#D97757',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.5)',
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontWeight: 600,
          letterSpacing: '0.3px',
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#0A0A0D',
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: '0.8125rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#25252e',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#353540',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D97757',
            borderWidth: 1,
            boxShadow: '0 0 0 3px rgba(217, 119, 87, 0.10)',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#25252e' },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFeatureSettings: '"tnum"',
        },
        '*:focus-visible': {
          outline: '1px solid #D97757',
          outlineOffset: 2,
          boxShadow: '0 0 0 3px rgba(217, 119, 87, 0.10)',
        },
        '@keyframes blink': {
          '0%, 49%': { opacity: 1 },
          '50%, 100%': { opacity: 0 },
        },
        '@keyframes breathe': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.5 },
        },
        '@keyframes pulse': {
          '0%, 100%': { transform: 'scale(1)', opacity: 1 },
          '50%': { transform: 'scale(1.3)', opacity: 0.6 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
      },
    },
  },
});
